import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Download, RotateCcw, FileText, Eye, EyeOff, Plus, Trash2, Save, CheckCircle, List } from 'lucide-react';
import { fetchProposalProducts, fetchProposalSettings, seedProposalCatalog } from '../actions/proposalCatalog';
import { createProposal, updateProposal } from '../actions/proposals';
import VisualLockerLine, { getModuleImage } from '../components/VisualLockerLine';
import {
  LICENSE_TYPES, MAINTENANCE_TYPES, ZONES, LOCKER_RANGES,
  INSTALLATION_TYPES, LOCK_TYPES, SALE_MODES, fmt, fmtAlways, fmtPct
} from '../utils/proposalCatalog';
import '../styles/ProposalConfiguratorView.css';

// ─── Design Tokens ─────────────────────────────────────────
const TEAL      = '#0a4080';
const DARK      = '#001d3a';
const BG        = '#f4f6f9';
const CARD      = '#ffffff';
const BORDER    = '#e2e8f0';
const TEXT_1    = '#1e293b';
const TEXT_2    = '#64748b';
const TEXT_3    = '#94a3b8';
const GREEN     = '#10b981';
const AMBER     = '#f59e0b';
const RED       = '#ef4444';

const TH_BG    = '#f0fdfa';
const TH_COLOR = '#0e7490';
const ROW_HL   = '#f0f9ff';

// ─── Shared inline style fragments ────────────────────────
const cardStyle    = { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: 24 };
const labelStyle   = { fontSize: 12, fontWeight: 600, color: TEXT_2, marginBottom: 4, display: 'block' };
const inputStyle   = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT_1, outline: 'none', boxSizing: 'border-box' };
const selectStyle  = { ...inputStyle, background: '#fff', cursor: 'pointer' };
const sectionTitle = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: TEXT_2, marginBottom: 16 };

// ─── Component ─────────────────────────────────────────────
class ProposalConfiguratorView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Client info
      clientName: '',
      clientCompany: '',
      clientCIF: '',
      clientAddress: '',
      proposalRef: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      notes: '',
      // Configuration
      licenseType: 'PRO',
      maintenanceType: 'ESTANDAR',
      zone: 'ZONA_II',
      lockerRange: '0-500',
      customization: true,
      installationType: 'interior',
      catalogTab: 'interior',
      lockType: 'STANDARD',
      saleMode: 'purchase',
      // Rental / Revenue Share fields
      contractDuration: 60,
      depositMonths: 4,
      commissionPct: 0,
      minAnnualCommission: 0,
      installationCost: 'Presupuesto pendiente',
      conditions: '',
      // Discounts
      discountProduct: 0,
      discountMaintenance: 0,
      discountLicense: 0,
      discountRental: 0,
      // Added line items: [ { productId, code, name, lockers, unitPrice, unitRent, qty } ]
      cabinetItems: [],
      posItems: [],
      // Visual configurator line
      configLine: [],
      // Module input mode: 'visual' | 'list'
      moduleMode: 'visual',
      // Selector state
      selectedCabinetCode: '',
      selectedPosCode: '',
      // PDF / preview
      generatingPdf: false,
      showPreview: false,
      // Save state
      savedProposalId: null,
      savingProposal: false,
      saveSuccess: false,
    };
    this.printRef = React.createRef();
    this.printCoverRef = React.createRef();
    this.printVisualRef = React.createRef();
    this.printDataRef = React.createRef();
  }

  componentDidMount() {
    this.props.fetchProposalProducts();
    this.props.fetchProposalSettings();
    // Load saved proposal if navigated from ProposalsView
    const loaded = this.props.location && this.props.location.state && this.props.location.state.loadedProposal;
    if (loaded) {
      this.loadProposal(loaded);
    }
  }

  componentDidUpdate(prevProps) {
    // Auto-seed if no products loaded
    if (this.props.products && this.props.products.length === 0 && prevProps.products === null) {
      this.props.seedProposalCatalog().then(() => this.props.fetchProposalProducts());
    }
  }

  // ─── Helpers: get DB-sourced data ──────────────────────
  getProducts(category) {
    const { products } = this.props;
    if (!products) return [];
    return products.filter(p => p.category === category);
  }

  getSetting(path, fallback) {
    const { settings } = this.props;
    if (!settings) return fallback;
    const parts = path.split('.');
    let v = settings;
    for (const k of parts) {
      if (v == null) return fallback;
      v = v[k];
    }
    return v ?? fallback;
  }

  getLicensePricePerPOS() {
    const { licenseType } = this.state;
    if (licenseType === 'NONE') return 0;
    return this.getSetting(`licensePricing.${licenseType}.perPOS`, licenseType === 'PRO' ? 150 : 0);
  }

  getMaintenanceRate() {
    const { maintenanceType } = this.state;
    if (maintenanceType === 'NONE') return 0;
    return this.getSetting(`maintenancePricing.${maintenanceType}`, 58.82);
  }

  priceField(product) {
    return this.state.installationType === 'exterior' ? product.priceExterior : product.priceInterior;
  }

  rentField(product) {
    return this.state.installationType === 'exterior' ? product.rentExterior : product.rentInterior;
  }

  // ─── Add / Remove Items ────────────────────────────────
  addItem = (category) => {
    const code = category === 'cabinet' ? this.state.selectedCabinetCode : this.state.selectedPosCode;
    if (!code) return;
    const product = (this.props.products || []).find(p => p.code === code);
    if (!product) return;

    const isExt = this.state.installationType === 'exterior';
    const item = {
      productId: product._id,
      code: product.code,
      envCode: isExt ? (product.codeOutdoor || product.code) : (product.codeIndoor || product.code),
      name: product.name,
      lockers: product.lockers,
      unitPrice: this.priceField(product),
      unitRent: this.rentField(product),
      personalizationPrice: product.personalizationPrice || 0,
      qty: 1,
    };

    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: [...prev.cabinetItems, item], selectedCabinetCode: '' }));
    } else {
      this.setState(prev => ({ posItems: [...prev.posItems, item], selectedPosCode: '' }));
    }
  };

  removeItem = (category, index) => {
    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: prev.cabinetItems.filter((_, i) => i !== index) }));
    } else {
      this.setState(prev => ({ posItems: prev.posItems.filter((_, i) => i !== index) }));
    }
  };

  setItemQty = (category, index) => (e) => {
    const v = Math.max(1, parseInt(e.target.value) || 1);
    if (category === 'cabinet') {
      this.setState(prev => {
        const items = [...prev.cabinetItems];
        items[index] = { ...items[index], qty: v };
        return { cabinetItems: items };
      });
    } else {
      this.setState(prev => {
        const items = [...prev.posItems];
        items[index] = { ...items[index], qty: v };
        return { posItems: items };
      });
    }
  };

  // Sync visual line → cabinetItems + posItems
  onVisualLineChange = (line) => {
    const { products } = this.props;
    const isExt = this.state.installationType === 'exterior';
    const counts = {};
    line.forEach(m => { counts[m.code] = (counts[m.code] || 0) + 1; });
    const buildItems = (category) => Object.entries(counts).map(([code, qty]) => {
      const prod = (products || []).find(p => p.code === code && p.category === category);
      if (!prod) return null;
      return {
        productId: prod._id, code, envCode: isExt ? (prod.codeOutdoor || code) : (prod.codeIndoor || code),
        name: prod.name, lockers: prod.lockers, category: prod.category,
        unitPrice: isExt ? prod.priceExterior : prod.priceInterior,
        unitRent:  isExt ? prod.rentExterior  : prod.rentInterior,
        personalizationPrice: prod.personalizationPrice || 0,
        qty,
      };
    }).filter(Boolean);
    this.setState({ configLine: line, cabinetItems: buildItems('cabinet'), posItems: buildItems('pos') });
  };

  // When env tab changes
  handleInstallationChange = (tabValue) => {
    // Switching to POS tab: only change catalog view, never touch installationType
    if (tabValue === 'pos') {
      this.setState({ catalogTab: 'pos' });
      return;
    }

    // Switching between interior / exterior
    const installationType = tabValue;
    const isExt = installationType === 'exterior';
    const products = this.props.products || [];

    const rePriceItems = (items) => items.map(item => {
      const prod = products.find(p => p.code === item.code);
      if (!prod) return item;
      return { ...item, envCode: isExt ? (prod.codeOutdoor || item.code) : (prod.codeIndoor || item.code), unitPrice: isExt ? prod.priceExterior : prod.priceInterior, unitRent: isExt ? prod.rentExterior : prod.rentInterior };
    });

    // Re-price all items from visual line with new env pricing
    const { configLine } = this.state;
    const counts = {};
    configLine.forEach(m => { counts[m.code] = (counts[m.code] || 0) + 1; });
    const buildItems = (category) => Object.entries(counts).map(([code, qty]) => {
      const prod = products.find(p => p.code === code && p.category === category);
      if (!prod) return null;
      return {
        productId: prod._id, code, envCode: isExt ? (prod.codeOutdoor || code) : (prod.codeIndoor || code),
        name: prod.name, lockers: prod.lockers, category: prod.category,
        unitPrice: isExt ? prod.priceExterior : prod.priceInterior,
        unitRent:  isExt ? prod.rentExterior  : prod.rentInterior,
        personalizationPrice: prod.personalizationPrice || 0,
        qty,
      };
    }).filter(Boolean);

    this.setState(prev => ({
      installationType,
      catalogTab: tabValue,
      cabinetItems: configLine.length > 0 ? buildItems('cabinet') : rePriceItems(prev.cabinetItems),
      posItems:     configLine.length > 0 ? buildItems('pos')     : rePriceItems(prev.posItems),
      selectedPosCode: '',
    }));
  };

  // ─── Load saved proposal into form ───────────────────
  loadProposal = (p) => {
    this.setState({
      savedProposalId: p._id || null,
      clientName:    p.clientName    || '',
      clientCompany: p.clientCompany || '',
      clientCIF:     p.clientCIF     || '',
      clientAddress: p.clientAddress || '',
      proposalRef:   p.proposalRef   || '',
      proposalDate:  p.proposalDate  || new Date().toISOString().slice(0, 10),
      notes:         p.notes         || '',
      conditions:    p.conditions    || '',
      licenseType:     p.licenseType     || 'PRO',
      maintenanceType: p.maintenanceType || 'ESTANDAR',
      zone:            p.zone            || 'ZONA_II',
      lockerRange:     p.lockerRange     || '0-500',
      customization:   p.customization !== undefined ? p.customization : true,
      installationType: p.installationType || 'interior',
      catalogTab:      p.installationType || 'interior',
      lockType:        p.lockType   || 'STANDARD',
      saleMode:        p.saleMode   || 'purchase',
      moduleMode:      p.moduleMode || 'visual',
      contractDuration:    p.contractDuration    !== undefined ? p.contractDuration    : 60,
      depositMonths:       p.depositMonths       !== undefined ? p.depositMonths       : 4,
      commissionPct:       p.commissionPct       !== undefined ? p.commissionPct       : 0,
      minAnnualCommission: p.minAnnualCommission !== undefined ? p.minAnnualCommission : 0,
      installationCost:    p.installationCost    || 'Presupuesto pendiente',
      discountProduct:     p.discountProduct     !== undefined ? p.discountProduct     : 0,
      discountMaintenance: p.discountMaintenance !== undefined ? p.discountMaintenance : 0,
      discountLicense:     p.discountLicense     !== undefined ? p.discountLicense     : 0,
      discountRental:      p.discountRental      !== undefined ? p.discountRental      : 0,
      cabinetItems: p.cabinetItems || [],
      posItems:     p.posItems     || [],
      configLine:   p.configLine   || [],
    });
  };

  // ─── Collect current form state as proposal payload ───
  gatherProposalData = () => {
    const s = this.state;
    return {
      clientName:    s.clientName,
      clientCompany: s.clientCompany,
      clientCIF:     s.clientCIF,
      clientAddress: s.clientAddress,
      proposalRef:   s.proposalRef,
      proposalDate:  s.proposalDate,
      notes:         s.notes,
      conditions:    s.conditions,
      licenseType:     s.licenseType,
      maintenanceType: s.maintenanceType,
      zone:            s.zone,
      lockerRange:     s.lockerRange,
      customization:   s.customization,
      installationType: s.installationType,
      lockType:        s.lockType,
      saleMode:        s.saleMode,
      moduleMode:      s.moduleMode,
      contractDuration:    s.contractDuration,
      depositMonths:       s.depositMonths,
      commissionPct:       s.commissionPct,
      minAnnualCommission: s.minAnnualCommission,
      installationCost:    s.installationCost,
      discountProduct:     s.discountProduct,
      discountMaintenance: s.discountMaintenance,
      discountLicense:     s.discountLicense,
      discountRental:      s.discountRental,
      cabinetItems: s.cabinetItems,
      posItems:     s.posItems,
      configLine:   s.configLine,
      createdByName: this.props.fullname || '',
      createdById:   this.props.authId   || '',
    };
  };

  // ─── Save proposal ────────────────────────────────────
  saveProposal = async () => {
    const { savedProposalId } = this.state;
    this.setState({ savingProposal: true, saveSuccess: false });
    try {
      const data = this.gatherProposalData();
      if (savedProposalId) {
        await this.props.updateProposal(savedProposalId, data);
      } else {
        const result = await this.props.createProposal(data);
        // store the returned _id so subsequent saves become updates
        if (result && result.response && result.response._id) {
          this.setState({ savedProposalId: result.response._id });
        }
      }
      this.setState({ saveSuccess: true });
      setTimeout(() => this.setState({ saveSuccess: false }), 3000);
    } catch (err) {
      console.error('Error saving proposal:', err);
    } finally {
      this.setState({ savingProposal: false });
    }
  };

  // ─── Generic handlers ──────────────────────────────────
  set = (field) => (e) => this.setState({ [field]: e.target.value });

  setNum = (field) => (e) => {
    const v = parseFloat(e.target.value);
    this.setState({ [field]: isNaN(v) ? 0 : Math.max(0, Math.min(100, v)) });
  };

  resetForm = () => {
    this.setState({
      clientName: '', clientCompany: '', clientCIF: '', clientAddress: '',
      proposalRef: '', notes: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      licenseType: 'PRO', maintenanceType: 'ESTANDAR', zone: 'ZONA_II',
      lockerRange: '0-500', customization: true, installationType: 'interior',
      lockType: 'STANDARD', saleMode: 'purchase',
      contractDuration: 60, depositMonths: 4, commissionPct: 0, minAnnualCommission: 0,
      installationCost: 'Presupuesto pendiente', conditions: '',
      discountProduct: 0, discountMaintenance: 0, discountLicense: 0, discountRental: 0,
      cabinetItems: [], posItems: [], selectedCabinetCode: '', selectedPosCode: '',
      configLine: [], catalogTab: 'interior', moduleMode: 'visual',
    });
  };

  setModuleMode = (mode) => {
    if (mode === this.state.moduleMode) return;
    if (mode === 'visual') {
      // Switching to visual: reset everything so the line starts fresh
      this.setState({ moduleMode: mode, configLine: [], cabinetItems: [], posItems: [], catalogTab: this.state.installationType });
    } else {
      // Switching to list: keep existing items, just clear the visual line
      this.setState({ moduleMode: mode, configLine: [] });
    }
  };

  // ─── Calculations ──────────────────────────────────────
  calculateTotals = () => {
    const { cabinetItems, posItems, customization, saleMode,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            contractDuration, depositMonths } = this.state;

    const sumItems = (items) => items.reduce((acc, it) => ({
      units: acc.units + it.qty,
      lockers: acc.lockers + it.qty * it.lockers,
      sale: acc.sale + it.qty * it.unitPrice,
      rent: acc.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    const cab = sumItems(cabinetItems);
    const pos = sumItems(posItems);

    const totalLockers = cab.lockers + pos.lockers;
    const baseSale     = cab.sale + pos.sale;
    const baseRent     = cab.rent + pos.rent;

    const licenseMonthlyPerPOS = this.getLicensePricePerPOS();
    const totalPOS         = pos.units;
    const baseLicenseMonth = Math.round(totalPOS * licenseMonthlyPerPOS);
    const baseMaintYear    = Math.round(totalLockers * this.getMaintenanceRate());

    const dp = 1 - discountProduct / 100;
    const dm = 1 - discountMaintenance / 100;
    const dl = 1 - discountLicense / 100;
    const dr = 1 - discountRental / 100;

    const sale             = Math.round(baseSale * dp);
    const licenseMonthly   = Math.round(baseLicenseMonth * dl);
    const licenseAnnual    = licenseMonthly * 12;
    const maintenanceAnnual = Math.round(baseMaintYear * dm);
    const rentMonthly      = Math.round(baseRent * dr);
    const rentWithLicense  = rentMonthly + Math.round(baseLicenseMonth * dl);
    const personalization  = customization ? [...cabinetItems, ...posItems].reduce((s, it) => {
      return s + it.qty * (it.personalizationPrice || 0);
    }, 0) : 0;
    const deposit          = rentWithLicense * depositMonths;

    return {
      totalCabinets: cab.units, totalKiosks: pos.units, totalLockers,
      totalUnits: cab.units + pos.units,
      sale, licenseMonthly, licenseAnnual, maintenanceAnnual,
      rentMonthly, rentWithLicense, personalization, deposit,
      cabinetSale: cab.sale, cabinetRent: cab.rent, cabinetUnits: cab.units, cabinetLockers: cab.lockers,
      posSale: pos.sale, posRent: pos.rent, posUnits: pos.units,
    };
  };

  // ─── PDF Export ────────────────────────────────────────
  savePdf = async () => {
    const coverEl  = this.printCoverRef.current;
    const visualEl = this.printVisualRef.current;
    const dataEl   = this.printDataRef.current;
    if (!coverEl || !visualEl || !dataEl) return;
    this.setState({ generatingPdf: true });
    try {
      // A4 dimensions (mm) per orientation
      const PORTRAIT  = { w: 210, h: 297 };
      const LANDSCAPE = { w: 297, h: 210 };

      // Start the document in the orientation of the first page we emit.
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      // Render one DOM node to the PDF with a specific page orientation.
      // If its aspect ratio overflows one A4 page, slice vertically into
      // multiple pages of the SAME orientation.
      const renderNode = async (el, orientation, isFirstPage) => {
        const page = orientation === 'l' ? LANDSCAPE : PORTRAIT;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgW = page.w;
        const imgH = (canvas.height * imgW) / canvas.width;

        if (!isFirstPage) pdf.addPage('a4', orientation);

        if (imgH <= page.h) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH, undefined, 'NONE');
        } else {
          let pos = 0;
          pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
          let left = imgH - page.h;
          while (left > 0) {
            pos -= page.h;
            pdf.addPage('a4', orientation);
            pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
            left -= page.h;
          }
        }
      };

      // Page 1: cover page (portrait)
      await renderNode(coverEl, 'p', true);
      // Page 2: proposal data in PORTRAIT (narrower container → larger text)
      await renderNode(dataEl, 'p', false);
      // Page 3: visual module distribution + signature in LANDSCAPE (visual mode only)
      if (this.state.moduleMode === 'visual') {
        await renderNode(visualEl, 'l', false);
      }

      const ref  = this.state.proposalRef || 'propuesta';
      const date = this.state.proposalDate.replace(/-/g, '');
      pdf.save(`VACWAY_Propuesta_${ref}_${date}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      this.setState({ generatingPdf: false });
    }
  };

  // ─── Render: Visual item list (below drop zone) ───────
  renderVisualItemList() {
    const { cabinetItems, posItems, saleMode, installationType } = this.state;
    const allItems = [...cabinetItems, ...posItems];
    if (allItems.length === 0) return null;
    const isExt  = installationType === 'exterior';
    const showSale = saleMode === 'purchase';
    const priceLabel = showSale ? (isExt ? 'Precio Ext.' : 'Precio Int.') : (isExt ? 'Alq./mes Ext.' : 'Alq./mes Int.');
    const thS = { padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };
    const total = allItems.reduce((a, it) => ({
      units: a.units + it.qty, lockers: a.lockers + it.qty * it.lockers,
      sale: a.sale + it.qty * it.unitPrice, rent: a.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });
    return (
      <div style={{ borderTop: `1px solid ${BORDER}` }}>
        <div style={{ padding: '12px 24px 8px' }}>
          <div style={sectionTitle}>Productos configurados</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                <th style={{ ...thS, minWidth: 70 }}>Tipo</th>
                <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>
                <th style={{ ...thS, minWidth: 60 }}>Cant.</th>
                <th style={{ ...thS, minWidth: 60 }}>Taquillas</th>
                <th style={{ ...thS, minWidth: 85 }}>{showSale ? 'Total Venta' : 'Total Alq.'}</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? ROW_HL : '#fff' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.envCode || it.code})</span></td>
                  <td style={{ ...tdS, textAlign: 'left' }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: it.category === 'pos' ? '#fef3c7' : '#e0f2fe', color: it.category === 'pos' ? '#92400e' : '#0369a1' }}>
                      {it.category === 'pos' ? 'Kiosco' : 'Armario'}
                    </span>
                  </td>
                  <td style={tdS}>{fmtAlways(showSale ? it.unitPrice : it.unitRent)}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.qty}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.qty * it.lockers}</td>
                  <td style={{ ...tdS, fontWeight: 600, color: showSale ? TEAL : GREEN }}>{fmtAlways(it.qty * (showSale ? it.unitPrice : it.unitRent))}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Total</td>
                <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{total.units}</td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{total.lockers}</td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: showSale ? TEAL : GREEN }}>{fmtAlways(showSale ? total.sale : total.rent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ─── Render: Visual Configurator Section ──────────────
  renderVisualSection() {
    const { products } = this.props;
    const { installationType, catalogTab, configLine, moduleMode } = this.state;
    const envLocked = configLine.some(m => m.category === 'cabinet');
    const cabinets   = this.getProducts('cabinet');
    const posProducts = this.getProducts('pos');

    // ── Shared header bar ──────────────────────────────
    const modeSwitch = (
      <div style={{ padding: '14px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={sectionTitle}>Vista de Instalación</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {/* Mode switch */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: TEXT_2 }}>Modo:</span>
            {[{ v: 'visual', label: 'Visual' }, { v: 'list', label: 'Lista' }].map(({ v, label }) => (
              <button key={v} type="button" onClick={() => this.setModuleMode(v)}
                style={{
                  padding: '4px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: moduleMode === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                  color: moduleMode === v ? '#fff' : TEXT_2,
                }}>
                {label}
              </button>
            ))}
          </div>
          {/* Environment tabs – visual mode: Interior / Exterior / PdV */}
          {moduleMode === 'visual' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: TEXT_2 }}>Entorno:</span>
              {[
                { v: 'interior', label: 'Interior' },
                { v: 'exterior', label: 'Exterior' },
                { v: 'pos',      label: 'Puntos de venta' },
              ].map(({ v, label }) => {
                const isLocked = envLocked && v !== 'pos' && installationType !== v;
                return (
                  <button key={v} type="button"
                    disabled={isLocked}
                    onClick={() => !isLocked && this.handleInstallationChange(v)}
                    title={isLocked ? 'Limpia la linea para cambiar de entorno' : ''}
                    style={{
                      padding: '5px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                      cursor: isLocked ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                      background: catalogTab === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                      color: catalogTab === v ? '#fff' : TEXT_2,
                      opacity: isLocked ? 0.4 : 1,
                    }}>
                    {label}{isLocked ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>
          )}
          {/* Environment tabs – list mode: Interior / Exterior only */}
          {moduleMode === 'list' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: TEXT_2 }}>Entorno:</span>
              {[{ v: 'interior', label: 'Interior' }, { v: 'exterior', label: 'Exterior' }].map(({ v, label }) => (
                <button key={v} type="button" onClick={() => this.handleInstallationChange(v)}
                  style={{
                    padding: '5px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: installationType === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                    color: installationType === v ? '#fff' : TEXT_2,
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    // ── List mode ──────────────────────────────────────
    if (moduleMode === 'list') {
      return (
        <div>
          <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
            {modeSwitch}
          </div>
          {this.renderProductSection('Armarios de taquillas', 'cabinet', this.state.cabinetItems, cabinets)}
          {this.renderProductSection('Puntos de venta', 'pos', this.state.posItems, posProducts)}
        </div>
      );
    }

    // ── Visual mode ────────────────────────────────────
    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        {modeSwitch}
        <div style={{ padding: 20 }}>
          <VisualLockerLine
            products={products || []}
            env={catalogTab}
            lockedEnv={envLocked ? installationType : null}
            configLine={configLine}
            onChange={this.onVisualLineChange}
          />
        </div>
        {configLine.length > 0 && (
          <div style={{ padding: '8px 24px 14px', borderTop: `1px solid ${BORDER}`, fontSize: 12, color: TEXT_2 }}>
            <strong>{configLine.length}</strong> modulos colocados &nbsp;|&nbsp;
            <strong>{[...this.state.cabinetItems, ...this.state.posItems].reduce((s, it) => s + it.qty * it.lockers, 0)}</strong> taquillas totales
          </div>
        )}
        {this.renderVisualItemList()}
      </div>
    );
  }

  // ─── Render: Print Visual Line ─────────────────────────
  renderPrintVisualLine() {
    const { configLine, installationType } = this.state;
    if (!configLine || configLine.length === 0) return null;
    const env = installationType === 'exterior' ? 'exterior' : 'interior';
    const totalCm = configLine.reduce((s, m) => s + (m._imgWidth ?? 65.7), 0);
    const totalLockers = configLine.reduce((s, m) => s + (m.lockers || 0), 0);
    const totalDisplay = totalCm >= 100
      ? `${(totalCm / 100).toFixed(2).replace(/\.?0+$/, '')} m`
      : `${parseFloat(totalCm.toFixed(1))} cm`;
    const height = env === 'exterior' ? '1.94 m' : '2.035 m';

    // SVG viewBox aspect ratios (width/height) for explicit sizing in html2canvas
    const ASPECT = {
      'exterior_VLK06': 909/2590, 'exterior_VLM02': 548/1558, 'exterior_VLM04': 548/1558,
      'exterior_VLM04XL': 771/1151, 'exterior_VLM06': 548/1559, 'exterior_VLM06XL': 615/1349,
      'exterior_VLM10': 468/1330, 'exterior_VLM12': 493/1399,
      'interior_VLK08': 483/1322, 'interior_VLM04': 566/1551, 'interior_VLM04XL': 933/1370,
      'interior_VLM06': 567/1551, 'interior_VLM06XL': 544/1301,
      'interior_VLM10': 567/1552, 'interior_VLM12': 567/1551,
    };
    const IMG_H = 400;
    const getImgW = (m) => {
      const img = m.image || '';
      if (!img || img === 'qr') return 140; // QR square container
      const prefix = env === 'exterior' ? 'exterior' : 'interior';
      const key = `${prefix}_${img}`;
      const ratio = ASPECT[key] || 0.36;
      return Math.round(IMG_H * ratio);
    };

    const arrowL = { width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderRight: '6px solid #888', flexShrink: 0 };
    const arrowR = { width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft:  '6px solid #888', flexShrink: 0 };
    const arrowU = { width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #888', flexShrink: 0 };
    const arrowD = { width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop:    '6px solid #888', flexShrink: 0 };
    const lineH  = { flex: 1, height: 1, background: '#888' };
    const lineV  = { flex: 1, width: 1, background: '#888' };

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
          Vista de Instalación
        </div>

        {/* Width dimension line */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0 2px 0', marginRight: 36 }}>
          <span style={arrowL} />
          <span style={lineH} />
          <span style={{ padding: '0 10px', fontSize: 10, fontWeight: 700, color: '#444', whiteSpace: 'nowrap', fontFamily: 'Courier New, monospace' }}>
            {totalDisplay} — {totalLockers} taquillas
          </span>
          <span style={lineH} />
          <span style={arrowR} />
        </div>

        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Modules area */}
          <div style={{
            flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 0,
            background: 'linear-gradient(180deg, #f8fbff 0%, #edf3fa 100%)',
            border: '2px solid #d0d8e2', borderRadius: 8,
            padding: '16px 40px 0 40px', position: 'relative', minHeight: 420,
          }}>
            {/* Floor line */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
              background: 'linear-gradient(90deg, #c4cdd8, #a0adb8, #c4cdd8)', borderRadius: '2px 2px 0 0' }} />

            {configLine.map((m, idx) => {
              const tag = (m.code || m.name || '').replace(/^(POS_)?(INT_|EXT_)?/, '').split(/[\s-]/)[0];
              const w = getImgW(m);
              return (
                <div key={m.uid || idx} style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 1,
                  margin: 0, padding: 0, lineHeight: 0,
                }}>
                  {/* Use background-image: html2canvas respects background-size on sized divs */}
                  <div style={{
                    width: w, height: IMG_H,
                    backgroundImage: `url(${getModuleImage(m, env)})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center bottom',
                    flexShrink: 0,
                    ...(m.image === 'qr' || !m.image ? { backgroundSize: '70%', backgroundPosition: 'center center' } : {}),
                  }} />
                  <span style={{
                    fontSize: 8, fontWeight: 700, color: '#022040', textAlign: 'center',
                    whiteSpace: 'nowrap', lineHeight: 'normal',
                    margin: 0, padding: '2px 0 6px',
                  }}>
                    {tag}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Height dimension */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 6px', minWidth: 32, flexShrink: 0 }}>
            <span style={arrowU} />
            <span style={lineV} />
            <span style={{
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              fontSize: 9, fontWeight: 700, color: '#444', padding: '4px 0',
              fontFamily: 'Courier New, monospace', whiteSpace: 'nowrap',
            }}>
              {height}
            </span>
            <span style={lineV} />
            <span style={arrowD} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Hero ──────────────────────────────────────
  renderHero() {
    const { savedProposalId, proposalRef, clientName, savingProposal, saveSuccess, showPreview, generatingPdf } = this.state;
    const isEditing = !!savedProposalId;

    const badge = isEditing
      ? <span className="pconf-mode-badge editing">Editando propuesta</span>
      : <span className="pconf-mode-badge new">Nueva propuesta</span>;

    const subtitle = isEditing
      ? `${proposalRef ? `Ref: ${proposalRef}` : 'Sin referencia'}${clientName ? ` · ${clientName}` : ''}`
      : 'Configura y genera propuestas comerciales para instalaciones VACWAYlockers';

    return (
      <div className={`pconf-header${isEditing ? ' editing' : ''}`}>
        <div className="pconf-header-content">
          <div className="pconf-header-text">
            <h1>
              <FileText size={28} className="pconf-header-icon" />
              Configurador de Propuestas
              {badge}
            </h1>
            <span className="pconf-subtitle">
              {subtitle}
            </span>
          </div>
          <div className="pconf-header-actions">
            <button onClick={() => this.setState(p => ({ showPreview: !p.showPreview }))}
              className="pconf-header-btn outline">
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPreview ? 'Editor' : 'Vista previa'}
            </button>
            <button onClick={this.resetForm}
              className="pconf-header-btn outline">
              <RotateCcw size={14} /> Limpiar
            </button>
            <button
              onClick={this.saveProposal}
              disabled={savingProposal}
              className={`pconf-header-btn ${isEditing ? 'save-update' : 'outline'}`}
            >
              {saveSuccess
                ? <><CheckCircle size={14} /> Guardada</>
                : <><Save size={14} /> {savingProposal ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}</>}
            </button>
            <button
              onClick={() => this.props.history && this.props.history.push('/proposals')}
              className="pconf-header-btn outline"
              title="Ver todas las propuestas"
            >
              <List size={14} /> Propuestas
            </button>
            <button onClick={this.savePdf} disabled={generatingPdf}
              className="pconf-header-btn primary">
              <Download size={14} /> {generatingPdf ? 'Generando...' : 'Exportar PDF'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Summary Banner ────────────────────────────
  renderSummaryBanner(t) {
    const { licenseType, maintenanceType, customization, installationType,
            lockType, saleMode, contractDuration, depositMonths,
            commissionPct, minAnnualCommission, installationCost,
            cabinetItems, posItems } = this.state;
    const instLbl = INSTALLATION_TYPES.find(i => i.value === installationType)?.label || '';
    const lockLbl = LOCK_TYPES.find(l => l.value === lockType)?.label || '';
    const modeLbl = SALE_MODES.find(m => m.value === saleMode)?.label || '';

    const kv = (label, value, bold) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 13, color: TEXT_2 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: TEXT_1 }}>{value}</span>
      </div>
    );

    const sectionHdr = (label, color) => (
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: color || TEAL, margin: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color || TEAL, display: 'inline-block' }} />
        {label}
      </div>
    );

    const itemLine = (item, idx) => (
      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
        <span style={{ color: TEXT_2 }}>{item.name} <span style={{ color: TEXT_3 }}>×{item.qty}</span></span>
        <span style={{ fontWeight: 600, color: TEXT_1 }}>{item.qty * item.lockers} taq.</span>
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Left – Config overview */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Configuración Oferta</div>
          {kv('Módulos', t.totalCabinets + t.totalKiosks)}
          {kv('Taquillas', t.totalLockers)}
          {kv('Tipo Instalación', instLbl)}
          {kv('Tipo Cerradura', lockLbl)}
          {kv('Modo de Venta', modeLbl, true)}
          {kv('Licencia', licenseType)}
          {kv('Mantenimiento', MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label)}
          {kv('Personalización', customization ? 'Sí' : 'No')}
        </div>

        {/* Center – Selected modules & kiosks */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Productos Seleccionados</div>
          {cabinetItems.length > 0 && (
            <>
              {sectionHdr('Armarios')}
              {cabinetItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {posItems.length > 0 && (
            <>
              {sectionHdr('Puntos de venta')}
              {posItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {cabinetItems.length === 0 && posItems.length === 0 && (
            <div style={{ padding: '20px 0', textAlign: 'center', color: TEXT_3, fontSize: 13, fontStyle: 'italic' }}>
              Sin productos añadidos
            </div>
          )}
        </div>

        {/* Right – CAPEX / OPEX */}
        <div style={cardStyle}>
          {saleMode === 'purchase' && (
            <>
              {sectionHdr('CAPEX')}
              {kv('Taquillas Smart VACWAYlockers (' + instLbl + ')', fmtAlways(t.sale), true)}
              {kv('Personalización' + (!customization ? ' (no incluida)' : ''), customization ? fmtAlways(t.personalization) : '-')}
              {kv('Instalación, formación y puesta en marcha', installationCost)}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias (' + licenseType + ') [mes]', fmtAlways(t.licenseMonthly))}
              {licenseType !== 'NONE' && kv('Licencias (' + licenseType + ') [año]', fmtAlways(t.licenseAnnual))}
              {maintenanceType !== 'NONE' && kv('Mantenimiento [año]', fmtAlways(t.maintenanceAnnual))}
            </>
          )}

          {saleMode === 'rental' && (
            <>
              {sectionHdr('ALQUILER')}
              {kv('Alquiler mensual (' + instLbl + ')', fmtAlways(t.rentWithLicense), true)}
              {kv('Depósito (' + depositMonths + ' meses)', fmtAlways(t.deposit))}
              {kv('Duración contrato', contractDuration + ' meses')}
              {kv('Personalización' + (!customization ? ' (no incluida)' : ''), customization ? fmtAlways(t.personalization) : '-')}
              {kv('Instalación, formación y puesta en marcha', installationCost)}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias (' + licenseType + ')', 'Incluidas')}
              {maintenanceType !== 'NONE' && kv('Mantenimiento [año]', fmtAlways(t.maintenanceAnnual))}
            </>
          )}

          {saleMode === 'revenue_share' && (
            <>
              {sectionHdr('REVENUE SHARE')}
              {kv('Duración contrato', contractDuration + ' meses')}
              {kv('Comisión VACWAY', fmtPct(commissionPct))}
              {kv('Comisión mínima anual', minAnnualCommission > 0 ? fmtAlways(minAnnualCommission) : '-')}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : '-')}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias', 'Incluidas')}
              {maintenanceType !== 'NONE' && kv('Mantenimiento', 'Incluido')}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Render: Input Section ─────────────────────────────
  renderInputSection() {
    const { clientName, clientCompany, clientCIF, clientAddress,
            proposalRef, proposalDate, notes,
            licenseType, maintenanceType, zone, lockerRange, customization, installationType,
            lockType, saleMode, contractDuration, depositMonths,
            commissionPct, minAnnualCommission, installationCost, conditions,
            discountProduct, discountMaintenance, discountLicense, discountRental } = this.state;

    const field = (lbl, value, onChange, type = 'text', extra = {}) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <input type={type} value={value} onChange={onChange} style={{ ...inputStyle, ...extra }} />
      </div>
    );

    const sel = (lbl, value, onChange, options) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <select value={value} onChange={onChange} style={selectStyle}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );

    const toggle2 = (lbl, value, onChange, options) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
          {options.map(o => {
            const active = o.value === value;
            return (
              <button key={o.value} type="button"
                onClick={() => onChange({ target: { value: o.value } })}
                style={{
                  flex: 1, padding: '9px 12px', fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  background: active ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#fff',
                  color: active ? '#fff' : TEXT_2,
                }}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    const toggleSwitch = (lbl, checked, onChange) => (
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{lbl}</label>
        <button type="button" onClick={() => onChange(!checked)}
          style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: checked ? '#0a4080' : '#cbd5e1', position: 'relative', transition: 'background 0.2s',
            flexShrink: 0,
          }}>
          <span style={{
            position: 'absolute', top: 2, left: checked ? 22 : 2,
            width: 20, height: 20, borderRadius: '50%', background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
          }} />
        </button>
      </div>
    );

    const pctField = (lbl, value, onChange) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" max="100" step="0.5" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: 32 }} />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: TEXT_3 }}>%</span>
        </div>
      </div>
    );

    const numField = (lbl, value, onChange, suffix = '') => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: suffix ? 40 : 12 }} />
          {suffix && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: TEXT_3 }}>{suffix}</span>}
        </div>
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 20 }}>
        {/* Client Info */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Datos del Cliente</div>
          {field('Empresa / Razón social', clientCompany, this.set('clientCompany'))}
          {field('CIF / NIF', clientCIF, this.set('clientCIF'))}
          {field('Dirección', clientAddress, this.set('clientAddress'))}
          {field('Persona de contacto', clientName, this.set('clientName'))}
          {field('Referencia propuesta', proposalRef, this.set('proposalRef'))}
          {field('Fecha', proposalDate, this.set('proposalDate'), 'date')}
        </div>

        {/* Configuration */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Configuración</div>
          {toggle2('Tipo de Cerradura', lockType, this.set('lockType'), LOCK_TYPES)}
          {toggle2('Tipo de Licencia', licenseType, this.set('licenseType'), LICENSE_TYPES)}
          {toggle2('Tipo de Mantenimiento', maintenanceType, this.set('maintenanceType'), MAINTENANCE_TYPES)}
          {sel('Zona de Instalación', zone, this.set('zone'), ZONES)}
          {toggle2('Personalización', customization, (v) => this.setState({ customization: v === true || v === 'true' }), [{ value: true, label: 'Sí' }, { value: false, label: 'No' }])}
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Observaciones</label>
            <textarea value={notes} onChange={this.set('notes')} rows={2}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        {/* Sale Mode + Conditions */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Modo de Venta</div>
          {toggle2('Modo', saleMode, this.set('saleMode'), SALE_MODES)}

          {(saleMode === 'rental' || saleMode === 'revenue_share') && (
            numField('Duración contrato', contractDuration, (e) => this.setState({ contractDuration: Math.max(1, parseInt(e.target.value) || 1) }), 'meses')
          )}
          {saleMode === 'rental' && (
            numField('Meses de depósito', depositMonths, (e) => this.setState({ depositMonths: Math.max(0, parseInt(e.target.value) || 0) }), 'meses')
          )}
          {saleMode === 'revenue_share' && (
            <>
              {pctField('Comisión VACWAY', commissionPct, (e) => { const v = parseFloat(e.target.value); this.setState({ commissionPct: isNaN(v) ? 0 : Math.max(0, Math.min(100, v)) }); })}
              {numField('Comisión mínima anual', minAnnualCommission, (e) => this.setState({ minAnnualCommission: Math.max(0, parseFloat(e.target.value) || 0) }), '€')}
            </>
          )}

          {field('Instalación y puesta en marcha', installationCost, this.set('installationCost'))}

          <div style={sectionTitle}>Descuentos</div>
          {pctField('Descuento Producto', discountProduct, this.setNum('discountProduct'))}
          {saleMode !== 'revenue_share' && maintenanceType !== 'NONE' && pctField('Descuento Mantenimiento', discountMaintenance, this.setNum('discountMaintenance'))}
          {saleMode === 'purchase' && licenseType !== 'NONE' && pctField('Descuento Licencias', discountLicense, this.setNum('discountLicense'))}
          {saleMode !== 'purchase' && pctField('Descuento Alquiler', discountRental, this.setNum('discountRental'))}

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Condiciones</label>
            <textarea value={conditions} onChange={this.set('conditions')} rows={3}
              placeholder="Precio ex-works Cataluña. Transporte no incluido..."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Product Adder + Table ─────────────────────
  renderProductSection(title, category, items, availableProducts) {
    const selKey = category === 'cabinet' ? 'selectedCabinetCode' : 'selectedPosCode';
    const selectedCode = this.state[selKey];
    const isExt = this.state.installationType === 'exterior';
    const { saleMode } = this.state;
    const showSale = saleMode === 'purchase';
    const showRent = saleMode === 'rental' || saleMode === 'revenue_share';
    const priceLabel = showSale ? (isExt ? 'Precio Ext.' : 'Precio Int.') : (isExt ? 'Alq./mes Ext.' : 'Alq./mes Int.');

    const thS = { padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };

    // Subtotals
    const sub = items.reduce((a, it) => ({
      units: a.units + it.qty,
      lockers: a.lockers + it.qty * it.lockers,
      sale: a.sale + it.qty * it.unitPrice,
      rent: a.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={sectionTitle}>{title}</div>
          {/* Add product selector */}
          {(() => {
            const hasQR      = category === 'pos' && items.some(it => it.code === 'POS_QR');
            const hasNonQR   = category === 'pos' && items.some(it => it.code !== 'POS_QR');
            const posConflictMsg = hasQR
              ? 'El punto de venta QR no es compatible con otros puntos de venta'
              : hasNonQR
              ? 'No se puede añadir QR junto con otros puntos de venta'
              : null;
            const filteredProducts = availableProducts.filter(p => {
              if (items.some(it => it.code === p.code)) return false; // already added
              // Filter by environment (indoor/outdoor/both)
              if (p.env === 'indoor' && isExt) return false;
              if (p.env === 'outdoor' && !isExt) return false;
              if (category === 'pos') {
                if (hasQR && p.code !== 'POS_QR') return false;     // QR present → block others
                if (hasNonQR && p.code === 'POS_QR') return false;   // others present → block QR
              }
              return true;
            });
            return (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select value={selectedCode} onChange={(e) => this.setState({ [selKey]: e.target.value })}
                    style={{ ...selectStyle, width: 400, maxWidth: '100%' }}>
                    <option value="">— Seleccionar producto —</option>
                    {filteredProducts.map(p => (
                      <option key={p.code} value={p.code}>
                        {p.name} ({p.lockers} lockers) — {fmtAlways(showSale ? (isExt ? p.priceExterior : p.priceInterior) : (isExt ? p.rentExterior : p.rentInterior))}{showRent ? '/mes' : ''}
                      </option>
                    ))}
                  </select>
                  <button onClick={() => this.addItem(category)} disabled={!selectedCode}
                    style={{ background: selectedCode ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : BORDER, border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: selectedCode ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={14} /> Añadir
                  </button>
                </div>
                {posConflictMsg && (
                  <div style={{ marginTop: 8, fontSize: 12, color: AMBER, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    ⚠ {posConflictMsg}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr>
                  <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                  <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>
                  <th style={{ ...thS, minWidth: 60 }}>Cantidad</th>
                  <th style={{ ...thS, minWidth: 60 }}>Taquillas</th>
                  <th style={{ ...thS, minWidth: 85 }}>{showSale ? 'Total Venta' : 'Total Alquiler'}</th>
                  <th style={{ ...thS, minWidth: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} style={{ background: ROW_HL }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.envCode || it.code})</span></td>
                    <td style={tdS}>{fmtAlways(showSale ? it.unitPrice : it.unitRent)}</td>
                    <td style={{ ...tdS, padding: '4px 4px' }}>
                      <input type="number" min="1" value={it.qty} onChange={this.setItemQty(category, idx)}
                        style={{ width: 52, padding: '6px 4px', borderRadius: 6, border: `1px solid #0a4080`, textAlign: 'center', fontSize: 13, outline: 'none', background: '#fff' }} />
                    </td>
                    <td style={{ ...tdS, fontWeight: 600 }}>{it.qty * it.lockers}</td>
                    <td style={{ ...tdS, fontWeight: 600, color: showSale ? TEAL : GREEN }}>{fmtAlways(it.qty * (showSale ? it.unitPrice : it.unitRent))}</td>
                    <td style={tdS}>
                      <button onClick={() => this.removeItem(category, idx)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: RED, padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {/* Subtotal */}
                <tr style={{ background: '#f8fafc' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Subtotal</td>
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{sub.units}</td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{sub.lockers}</td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: showSale ? TEAL : GREEN }}>{fmtAlways(showSale ? sub.sale : sub.rent)}</td>
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {items.length === 0 && (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
            Selecciona un producto y pulsa "Añadir" para agregar elementos a la propuesta
          </div>
        )}
      </div>
    );
  }

  // ─── Render: Grand Total ───────────────────────────────
  renderGrandTotal(t) {
    const { saleMode, licenseType, maintenanceType } = this.state;
    const c = { padding: '12px 16px', textAlign: 'center', fontWeight: 700, fontSize: 15, color: '#fff' };
    const lbl = { fontSize: 11, color: '#a8c8e8', marginBottom: 4, fontWeight: 400 };

    return (
      <div style={{ marginBottom: 20, background: `linear-gradient(135deg, #022040 0%, #0a4080 100%)`, borderRadius: 12, overflow: 'hidden', border: `1px solid #0a4080` }}>
        <div style={{ padding: '14px 24px', borderBottom: `1px solid rgba(255,255,255,0.15)` }}>
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#fff' }}>Resumen Económico</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: saleMode === 'purchase' ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: 0 }}>
          <div style={c}>
            <div style={lbl}>Módulos</div>
            {t.totalUnits}
          </div>
          <div style={c}>
            <div style={lbl}>Taquillas</div>
            {t.totalLockers}
          </div>
          {saleMode === 'purchase' && (
            <div style={c}>
              <div style={lbl}>CAPEX (Venta)</div>
              {fmtAlways(t.sale)}
            </div>
          )}
          <div style={c}>
            <div style={{ ...lbl, color: GREEN }}>
              {saleMode === 'purchase' ? (licenseType === 'NONE' ? 'Licencia' : 'OPEX Licencias/año') : saleMode === 'rental' ? 'Alquiler/mes' : 'Revenue Share'}
            </div>
            <span style={{ color: GREEN }}>
              {saleMode === 'purchase'
                ? (licenseType === 'NONE' ? 'Sin Licencia' : fmtAlways(t.licenseAnnual))
                : saleMode === 'rental' ? fmtAlways(t.rentWithLicense) : fmtPct(this.state.commissionPct)}
            </span>
          </div>
          <div style={c}>
            <div style={{ ...lbl, color: AMBER }}>Personalización</div>
            <span style={{ color: AMBER }}>{fmtAlways(t.personalization)}</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Print Cover Page ──────────────────────────
  renderPrintCoverPage() {
    const { proposalRef, proposalDate, clientName, clientCompany } = this.state;
    const preparedBy = this.props.fullname || '';

    return (
      <div style={{
        width: '100%', height: 1018, display: 'flex', flexDirection: 'column',
        background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Background image */}
        <img
          src="/images/configurator/portada.png"
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />

        {/* Header with logo */}
        <div style={{ padding: '32px 48px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <img src="/images/logo_white.png" alt="VACWAYlockers" style={{ height: 36, width: 'auto' }} />
        </div>

        {/* Hero section — title centered in the upper-middle area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 60, position: 'relative', zIndex: 1 }}>
          {/* Line 1: OFERTA COMERCIAL */}
          <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center' }}>
            <span style={{ color: '#f59e0b' }}>OFERTA </span>
            <span style={{ color: '#ffffff' }}>COMERCIAL</span>
          </div>
          {/* Line 2: VACWAYlockers */}
          <div style={{ fontSize: 22, lineHeight: 1, letterSpacing: 4, textTransform: 'uppercase', textAlign: 'center', marginTop: 14 }}>
            <span style={{ color: '#ffffff', fontWeight: 300 }}>VACWAY</span>
            <span style={{ color: '#ffffff', fontWeight: 700, textTransform: 'none' }}>lockers</span>
          </div>
        </div>

        {/* Bottom info strip */}
        <div style={{ padding: '12px 48px', background: 'transparent', position: 'absolute', bottom: 30, left: 0, right: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>Realizado por</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{preparedBy || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>Referencia de la oferta</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{proposalRef || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>Fecha</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>
                {proposalDate ? new Date(proposalDate + 'T00:00:00').toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }

  // ─── Render: Print Header ──────────────────────────────
  // Shared header for print pages. `s` is a linear scale factor so the same
  // visual header can appear at a consistent physical size on both the narrow
  // portrait data page (s=1) and the wide landscape visual page (s≈2.75).
  renderPrintHeader(s = 1) {
    const { clientName, clientCompany, proposalRef, proposalDate } = this.state;
    const px = (n) => Math.round(n * s);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: px(20), borderBottom: `${Math.max(2, Math.round(3 * s))}px solid #0a4080`, paddingBottom: px(16) }}>
        <div>
          <img src="/images/logo_blue.png" alt="VACWAYlockers" style={{ height: px(32), width: 'auto', display: 'block' }} />
          <div style={{ fontSize: px(10), color: '#999', marginTop: px(2) }}>Smart Locker Solutions</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: px(11), color: '#666' }}>
          <div style={{ fontSize: px(16), fontWeight: 700, color: '#333', marginBottom: px(4) }}>PROPUESTA COMERCIAL</div>
          {proposalRef && <div>Ref: {proposalRef}</div>}
          <div>Fecha: {proposalDate}</div>
          {clientCompany && <div>Empresa: {clientCompany}</div>}
          {clientName && <div>Contacto: {clientName}</div>}
        </div>
      </div>
    );
  }

  // Company data + signature block (goes at the end of the visual page)
  renderPrintSignatureBlock(s = 1) {
    const { clientName, clientCompany, clientCIF, clientAddress } = this.state;
    const px = (n) => Math.round(n * s);
    return (
      <div style={{ marginTop: px(20), pageBreakBefore: 'auto' }}>
        <div style={{ fontSize: px(11), color: '#0e7490', fontStyle: 'italic', marginBottom: px(14) }}>
          Para la aceptación del presupuesto, por favor devuelva este documento firmado y sellado con la siguiente información:
        </div>

        <div style={{ fontSize: px(12), fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: px(10), display: 'flex', alignItems: 'center', gap: px(6) }}>
          <span style={{ color: '#0e7490' }}>➜</span> Datos de la Empresa
        </div>

        {[
          ['Razón social', clientCompany],
          ['CIF / NIF', clientCIF],
          ['Dirección del domicilio social', clientAddress],
          ['Nombre y apellidos', clientName],
        ].map(([lbl, val], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: px(8) }}>
            <span style={{ width: px(200), fontSize: px(11), color: '#555' }}>{lbl}</span>
            <div style={{ flex: 1, borderBottom: '1px solid #ccc', minHeight: px(20), fontSize: px(11), paddingBottom: px(2) }}>{val}</div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: px(30) }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: px(10), color: '#888', marginBottom: px(6) }}>Firma y sello de la empresa</div>
            <div style={{ width: px(200), height: px(80), border: '1px solid #ccc', borderRadius: px(6) }}></div>
            <div style={{ fontSize: px(10), color: '#888', marginTop: px(8) }}>Fecha: _______________</div>
          </div>
        </div>
      </div>
    );
  }

  // Page 1 content: header + all proposal data (portrait, s=1)
  // Page 2 content: header + visual module distribution + signature block
  //                 (landscape, s≈2.75 so header/signature match portrait size)
  renderPrintVisualPage() {
    // Scale so text on the 2800px wide landscape container prints at the
    // same physical mm size as text on the 720px portrait container:
    //   portrait: 210mm / 720px  ≈ 0.2917 mm/px
    //   landscape: 297mm / 2800px ≈ 0.1061 mm/px
    //   ratio ≈ 2.75
    const s = 2.75;
    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader(s)}
        {this.renderPrintVisualLine()}
        {this.renderPrintSignatureBlock(s)}
      </div>
    );
  }

  renderPrintDataPage(t, includeSignature = false) {
    const { notes,
            licenseType, maintenanceType, customization, installationType,
            lockType, saleMode, contractDuration, depositMonths,
            commissionPct, minAnnualCommission, installationCost, conditions,
            cabinetItems, posItems } = this.state;

    const instLbl = INSTALLATION_TYPES.find(i => i.value === installationType)?.label || '';
    const lockLbl = LOCK_TYPES.find(l => l.value === lockType)?.label || '';
    const modeLbl = SALE_MODES.find(m => m.value === saleMode)?.label || '';
    const maintLbl = MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label || '';

    const thP = { padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase', color: '#0e7490', background: '#e0f7fa', borderBottom: '2px solid #b2ebf2', whiteSpace: 'nowrap' };
    const tdP = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #e8e8e8', fontSize: 11, color: '#333' };

    const showSale = saleMode === 'purchase';

    const renderPrintTable = (title, items) => {
      if (items.length === 0) return null;
      const sub = items.reduce((a, it) => ({
        units: a.units + it.qty, lockers: a.lockers + it.qty * it.lockers,
        sale: a.sale + it.qty * it.unitPrice, rent: a.rent + it.qty * it.unitRent,
      }), { units: 0, lockers: 0, sale: 0, rent: 0 });

      return (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#0e7490' }}>➜</span> {title}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thP, textAlign: 'left', minWidth: 180 }}>Referencia</th>
                <th style={thP}>Cantidad</th>
                <th style={thP}>Taquillas</th>
                <th style={thP}>{showSale ? 'Precio Ud.' : 'Alquiler/mes Ud.'}</th>
                <th style={thP}>{showSale ? 'Total Venta' : 'Total Alquiler'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? '#f0f9ff' : '#fff' }}>
                  <td style={{ ...tdP, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 9, color: '#64748b', fontWeight: 400 }}>({it.envCode || it.code})</span></td>
                  <td style={{ ...tdP, fontWeight: 600 }}>{it.qty}</td>
                  <td style={tdP}>{it.qty * it.lockers}</td>
                  <td style={tdP}>{fmtAlways(showSale ? it.unitPrice : it.unitRent)}</td>
                  <td style={{ ...tdP, fontWeight: 600 }}>{fmtAlways(it.qty * (showSale ? it.unitPrice : it.unitRent))}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8f8f8' }}>
                <td style={{ ...tdP, textAlign: 'left', fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>Subtotal</td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>{sub.units}</td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>{sub.lockers}</td>
                <td style={{ ...tdP, borderTop: '2px solid #ccc', borderBottom: 'none' }}></td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none', color: '#0e7490' }}>{fmtAlways(showSale ? sub.sale : sub.rent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    };

    const printKv = (label, value) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
        <div style={{ flex: 1, padding: '5px 10px', fontSize: 11, color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#0e7490' }}>➜</span> {label}
        </div>
        <div style={{ flex: 1, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#333', borderLeft: '1px solid #eee' }}>{value}</div>
      </div>
    );

    const printSectionHdr = (label) => (
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', margin: '14px 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#333', display: 'inline-block' }} />
        {label}
      </div>
    );

    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader()}

        {/* ── Section: Installation Proposal ── */}
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 12, letterSpacing: 0.5 }}>
          Propuesta de Instalación
        </div>

        <div style={{ display: 'flex', gap: 30, marginBottom: 10, fontSize: 11 }}>
          <div>Total taquillas: <strong>{t.totalLockers}</strong></div>
          <div>Tipo: <strong>{instLbl}</strong></div>
          <div>Cerradura: <strong>{lockLbl}</strong></div>
        </div>

        {renderPrintTable('Armarios de taquillas', cabinetItems)}
        {renderPrintTable('Puntos de venta', posItems)}

        {/* ── Section: CAPEX / OPEX ── */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.5 }}>
            Oferta Económica y Condiciones
          </div>
          <div style={{ background: '#0e7490', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'inline-block' }}>
            {modeLbl}
          </div>
        </div>

        {saleMode === 'purchase' && (
          <div style={{ overflow: 'hidden', marginBottom: 16 }}>
            {printSectionHdr('CAPEX')}
            {printKv('Taquillas Smart VACWAYlockers (' + instLbl + ')', fmtAlways(t.sale) + ' + IVA')}
            {printKv('Personalización' + (!customization ? ' (no incluida)' : ''), customization ? fmtAlways(t.personalization) + ' (' + fmtAlways(Math.round(t.personalization / Math.max(t.totalUnits, 1))) + ' por módulo)' : 'No incluida')}
            {printKv('Instalación, formación y puesta en marcha', installationCost)}

            {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
            {licenseType !== 'NONE' && printKv('Licencias (' + licenseType + ')', fmtAlways(t.licenseMonthly) + '/mes — ' + fmtAlways(t.licenseAnnual) + '/año')}
            {maintenanceType !== 'NONE' && printKv('Mantenimiento (' + maintLbl + ')', fmtAlways(t.maintenanceAnnual) + ' anual')}
          </div>
        )}

        {saleMode === 'rental' && (
          <div style={{ border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
            {printSectionHdr('ALQUILER')}
            {printKv('Alquiler mensual VACWAYlockers (' + instLbl + ')', fmtAlways(t.rentWithLicense))}
            {printKv('Depósito ' + depositMonths + ' meses', fmtAlways(t.deposit))}
            {printKv('Duración del contrato (compromiso obligatorio)', Math.round(contractDuration / 12) + ' años (' + contractDuration + ' meses)')}
            {printKv('Personalización', customization ? fmtAlways(t.personalization) + ' (' + fmtAlways(Math.round(t.personalization / Math.max(t.totalUnits, 1))) + ' por módulo)' : 'No incluida')}
            {printKv('Instalación, formación y puesta en marcha', installationCost)}

            {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
            {licenseType !== 'NONE' && printKv('Licencias (' + licenseType + ')', 'Incluidas')}
            {maintenanceType !== 'NONE' && printKv('Mantenimiento (' + maintLbl + ')', fmtAlways(t.maintenanceAnnual) + ' anual')}
          </div>
        )}

        {saleMode === 'revenue_share' && (
          <div style={{ border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
            {printSectionHdr('REVENUE SHARE')}
            {printKv('Duración del contrato', Math.round(contractDuration / 12) + ' años (' + contractDuration + ' meses)')}
            {printKv('Comisión VACWAY', fmtPct(commissionPct))}
            {printKv('Cláusula de comisiones anuales mínimas', minAnnualCommission > 0 ? fmtAlways(minAnnualCommission) : '-')}
            {printKv('Personalización', customization ? fmtAlways(t.personalization) : 'No incluida')}

            {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
            {licenseType !== 'NONE' && printKv('Licencias', 'Incluidas')}
            {maintenanceType !== 'NONE' && printKv('Mantenimiento', 'Incluido')}
          </div>
        )}

        {/* Conditions */}
        {conditions && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#333', textTransform: 'uppercase', marginBottom: 6 }}>* Condiciones</div>
            <div style={{ fontSize: 10, color: '#555', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{conditions}</div>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6 }}>Observaciones</div>
            <div style={{ fontSize: 11, color: '#555', whiteSpace: 'pre-wrap' }}>{notes}</div>
          </div>
        )}

        {includeSignature && this.renderPrintSignatureBlock()}
      </div>
    );
  }

  renderPrintContent(t) {
    const { moduleMode } = this.state;
    // On-screen preview shows both printed pages stacked vertically.
    // In visual mode: cover + data page + visual distribution + signature.
    // In list mode: cover + data page (with signature) only.
    return (
      <>
        {this.renderPrintCoverPage()}
        <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
        {this.renderPrintDataPage(t, moduleMode === 'list')}
        {moduleMode === 'visual' && (
          <>
            <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
            {this.renderPrintVisualPage()}
          </>
        )}
      </>
    );
  }

  // ─── Render: Main ──────────────────────────────────────
  render() {
    const { products } = this.props;
    if (!products) {
      return <div style={{ padding: 60, textAlign: 'center', color: TEXT_2 }}>Cargando catálogo...</div>;
    }

    const totals = this.calculateTotals();
    const cabinets = this.getProducts('cabinet');
    const posProducts = this.getProducts('pos');

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", background: BG, minHeight: '100vh', padding: '24px 24px 60px' }}>
        {this.renderHero()}

        {this.state.showPreview ? (
          <div key="preview" style={{ margin: '0 auto' }}>
            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BORDER}`, padding: '30px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              {this.renderPrintContent(totals)}
            </div>
          </div>
        ) : (
          <div key="editor">
            {this.renderSummaryBanner(totals)}
            {this.renderInputSection()}
            {this.renderVisualSection()}
          </div>
        )}

        {/* Hidden print container for PDF export — two separate pages.
            Page 1 is wide (2800px) so the visual module layout keeps its
            fidelity. Page 2 is narrower (1400px) so the fixed px-based font
            sizes translate to larger, readable text on A4 landscape. */}
        <div style={{ position: 'fixed', left: 0, top: 0, zIndex: -9999, opacity: 0, pointerEvents: 'none' }}>
          <div ref={this.printCoverRef} style={{ width: 720, background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintCoverPage()}
          </div>
          <div ref={this.printVisualRef} style={{ width: 2800, background: '#fff', padding: '60px 80px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintVisualPage()}
          </div>
          <div ref={this.printDataRef} style={{ width: 720, background: '#fff', padding: '28px 32px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintDataPage(totals, this.state.moduleMode === 'list')}
          </div>
        </div>
      </div>
    );
  }
}

// ─── Redux Connection ────────────────────────────────────
const mapStateToProps = (state) => ({
  role:     state.auth.role,
  type:     state.auth.type,
  fullname: state.auth.fullname,
  authId:   state.auth.id,
  products: state.proposalCatalog.products,
  settings: state.proposalCatalog.settings,
  loading:  state.proposalCatalog.loading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchProposalProducts,
  fetchProposalSettings,
  seedProposalCatalog,
  createProposal,
  updateProposal,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProposalConfiguratorView);
